"use client";

import {
  type FC,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  AddCard,
  Body,
  Button,
  ExpansionPanel,
  Flex,
  Grid,
  Icon,
  IconButton,
  Modal,
  OptionalCard,
  Spacing,
  Stack,
  TabsNavigation,
  Text,
  Tooltip,
  useActions,
  useDebug,
  useForm,
  usePubSub,
  type DataFieldValue,
  type Tab,
  type UIAction,
} from "@huub-web/ui";

import { isObject } from "@huub-web/utils";
import {
  FormGroupContext,
  type UIAddModelChildAction,
  type UIDeleteModelChildAction,
} from "@huub-web/ui";

import type { TemplateCollection } from "../../types";
import { buildApiRequestValueAction } from "../../utils";
import { TemplatesParser } from "../../components";
import {
  TemplateParentContext,
  useTemplateParent,
} from "../../contexts/TemplateParentContext";
import { useTemplates } from "../../hooks/useTemplates";

export type ModelChildrenTemplateProps = {
  /**
   * Parent model ID. When present, child `{id}` placeholders are replaced
   * with `<id>.<childID>` to create nested data paths.
   */
  id?: string;
  /**
   * Prefix for generated child IDs (e.g. "collateral" produces "collateral__new_1").
   */
  idPrefix?: string;
  /**
   * Display name used as the label prefix (e.g. "Hypotheek" becomes "Hypotheek 1", "Hypotheek 2").
   */
  name: string;
  /**
   * Blueprint templates whose `id` fields contain `{id}` placeholders.
   * Each child renders these templates with `{id}` replaced by the child's unique ID.
   */
  templates: TemplateCollection;
  /**
   * Additional templates rendered after the delete button in each child.
   * `{id}` placeholders are replaced with the child's unique ID.
   */
  trailingTemplates?: TemplateCollection;
  /**
   * Layout variant for rendering children.
   * - "tabs": horizontal TabsNavigation (default)
   * - "flow": vertical ExpansionPanel per child, freely expandable/collapsible
   */
  variant?: "tabs" | "flow";
  /**
   * Prefilled field values per child instance, keyed by child ID. Each key
   * becomes a child ID and its value is a record of field names to prefilled
   * values (the part after `{id}.` in a form input ID, e.g. `companyId`).
   * Children added via the "+" button have no prefilled data.
   */
  data?: Record<string, Record<string, DataFieldValue>>;
  /**
   * When set, resolves `data` from the parent's data context using this key.
   * Useful for nested `modelChildren` inside shared blueprint templates where
   * `data` can't be set statically (e.g. `dataKey: "loanParts"` reads
   * `parentData.loanParts` as the child data record).
   * Ignored when `data` is explicitly provided.
   */
  dataKey?: string;
  /**
   * Nesting level used to stagger `modelChildrenUpdate` application.
   * The action handler delays pubSub publish by `updateLevel * 500ms`,
   * ensuring the parent has remounted before nested children receive
   * their update. Top-level instances leave this unset (no delay).
   */
  updateLevel?: number;
  /**
   * When provided, renders an OptionalCard + AddCard instead of immediately
   * showing the tab/flow structure. The user must click "add" to create the
   * first child. Checking the optional card triggers `action` and disables
   * the add button.
   */
  optional?: {
    action: UIAction;
    label: string;
    value?: boolean;
  };
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

type DeleteConfirmationModalProps = {
  /**
   * Display name shown in the confirmation message
   */
  name: string;
  /**
   * Whether the modal is visible
   */
  active: boolean;
  /**
   * Called when the user cancels deletion
   */
  onCancel: () => void;
  /**
   * Called when the user confirms deletion
   */
  onConfirm: () => void;
};

const DeleteConfirmationModal: FC<DeleteConfirmationModalProps> = ({
  name,
  active,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();

  if (!active) return null;

  return (
    <Modal placement="center">
      <Body>{t("deleteConfirmation", { name })}</Body>
      <Flex justify="space-between" align="flex-end" gap="s">
        <Button variant="background" border="default" onClick={onCancel}>
          {t("cancel")}
        </Button>
        <Button
          variant="error"
          trailing={<Icon icon="delete" size="s2" />}
          onClick={onConfirm}
        >
          {t("delete")}
        </Button>
      </Flex>
    </Modal>
  );
};

type ChildDataRecord = Record<string, Record<string, DataFieldValue>>;

/**
 * Resolves child data from the parent's data context when `dataKey` is set.
 * Returns the record if found, otherwise `undefined`.
 */
const resolveDataFromParent = ({
  dataKey,
  parent,
}: {
  dataKey?: string;
  parent?: { data?: Record<string, DataFieldValue> };
}): ChildDataRecord | undefined => {
  if (!dataKey || !parent?.data) return undefined;
  const value = parent.data[dataKey];
  return isObject(value) ? (value as unknown as ChildDataRecord) : undefined;
};

const ModelChildrenTemplateInner: FC<ModelChildrenTemplateProps> = ({
  id: parentID,
  idPrefix,
  name,
  templates,
  trailingTemplates,
  variant = "tabs",
  data: dataProp,
  dataKey,
  optional,
  ref,
}) => {
  const { t } = useTranslation();
  const { handleAction } = useActions();
  const { log } = useDebug();
  const form = useForm();
  const parent = useTemplateParent();
  const outerGroup = useContext(FormGroupContext);
  const pubSub = usePubSub();
  const { registerUIContext, deregisterUIContext } = useTemplates();

  const data = dataProp ?? resolveDataFromParent({ dataKey, parent });
  const prefix = idPrefix ? `${idPrefix}_` : "";
  const counterRef = useRef(0);

  const hasInitialData = data ? Object.keys(data).length > 0 : false;

  const [showAddCard, setShowAddCard] = useState<boolean>(
    () => !!optional && !hasInitialData,
  );
  const [isOptionalChecked, setIsOptionalChecked] = useState(
    optional?.value ?? false,
  );

  const [childIDs, setChildIDs] = useState<string[]>(() => {
    if (optional && !hasInitialData) return [];
    const keys = data ? Object.keys(data) : [];
    if (keys.length > 0) return keys;
    counterRef.current = 1;
    return [`${prefix}__new_1`];
  });
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (optional && !hasInitialData) return "";
    const keys = data ? Object.keys(data) : [];
    return keys.length > 0 ? keys[0] : `${prefix}__new_1`;
  });

  /**
   * Ref mirror of activeTab so functional state updaters and PubSub
   * handlers can read the latest value without capturing a stale closure.
   */
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  const [pendingDeleteID, setPendingDeleteID] = useState<string | null>(null);
  const [openPanels, setOpenPanels] = useState<Set<string>>(
    () => new Set(data ? [] : [`${prefix}__new_1`]),
  );

  const contentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [childErrors, setChildErrors] = useState<Record<string, boolean>>({});

  /**
   * Build a stable DOM id for a child panel, used for scrollTo targeting.
   */
  const getChildElementId = useCallback(
    (childID: string) => {
      const base = parentID ? `${parentID}-${childID}` : childID;
      return `model-child-${base}`;
    },
    [parentID],
  );

  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const childID of childIDs) {
      const el = contentRefs.current[childID];
      next[childID] = !!el?.querySelector("[data-error]");
    }
    setChildErrors(next);
  }, [childIDs, form?.errors]);

  // Register flow children as scrollTo content items for agent awareness
  const flowContentId = parentID
    ? `${parentID}-${name}-children`
    : `${name}-children`;
  useEffect(() => {
    if (variant !== "flow" || childIDs.length === 0) return;
    const content = childIDs.map((childID, index) => ({
      type: "data-item" as const,
      label: `${name} ${index + 1}`,
      action: {
        type: "scrollTo" as const,
        payload: { elementId: getChildElementId(childID) },
      },
    }));
    registerUIContext?.(flowContentId, {
      type: "page",
      content,
    });
    return () => {
      deregisterUIContext?.(flowContentId);
    };
  }, [
    childIDs,
    variant,
    name,
    flowContentId,
    getChildElementId,
    registerUIContext,
    deregisterUIContext,
  ]);

  const handleOptionalChange = useCallback(
    (checked: boolean) => {
      setIsOptionalChecked(checked);
      if (!optional?.action) return;

      const action = buildApiRequestValueAction({
        action: optional.action,
        value: checked,
      });

      log("purple", "ModelChildrenTemplate", "optional action", action);
      handleAction(action);
    },
    [optional, handleAction, log],
  );

  const handleAddFromCard = useCallback(() => {
    counterRef.current += 1;
    const newID = `${prefix}__new_${counterRef.current}`;
    setChildIDs([newID]);
    setActiveTab(newID);
    setOpenPanels(new Set([newID]));
    setShowAddCard(false);
  }, [prefix]);

  const handlePanelOpenChange = useCallback(
    ({ id, open }: { id: string; open: boolean }) => {
      setOpenPanels((prev) => {
        const next = new Set(prev);
        if (open) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );

  // Listen for expandChild PubSub events to auto-expand child panels
  useEffect(() => {
    if (variant !== "flow") return;
    const unsubscribe = pubSub.subscribe("expandChild", (payload) => {
      if (!payload?.elementId) return;
      const matchingChild = childIDs.find(
        (cid) => getChildElementId(cid) === payload.elementId,
      );
      if (matchingChild) {
        handlePanelOpenChange({ id: matchingChild, open: true });
      }
    });
    return unsubscribe;
  }, [childIDs, variant, pubSub, getChildElementId, handlePanelOpenChange]);

  const handleAdd = useCallback(() => {
    counterRef.current += 1;
    const newID = `${prefix}__new_${counterRef.current}`;
    setChildIDs((prev) => [...prev, newID]);
    setActiveTab(newID);
    setOpenPanels((prev) => new Set(prev).add(newID));
  }, [prefix]);

  /**
   * Applies the shared child-removal state transitions for a given ID.
   * Used by both the confirmation modal path and the agent-driven PubSub path.
   */
  const removeChild = useCallback(
    (deleteId: string) => {
      setChildIDs((prev) => {
        const next = prev.filter((id) => id !== deleteId);
        if (next.length === 0 && optional) {
          setShowAddCard(true);
          setActiveTab("");
        } else if (activeTabRef.current === deleteId && next.length > 0) {
          setActiveTab(next[0]);
        }
        return next;
      });
      setOpenPanels((prev) => {
        const next = new Set(prev);
        next.delete(deleteId);
        return next;
      });
    },
    [optional],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!pendingDeleteID) {
      return;
    }
    removeChild(pendingDeleteID);
    setPendingDeleteID(null);
  }, [pendingDeleteID, removeChild]);

  const canDelete = optional ? true : childIDs.length > 1;

  // Stable target ID for PubSub events — identifies this ModelChildren instance
  const targetId = parentID ?? name;

  // Register add/delete actions for agent awareness
  const actionSliceId = `${targetId}-${name}-actions`;
  useEffect(() => {
    const content: Array<{
      type: "data-item";
      label: string;
      action: UIAddModelChildAction | UIDeleteModelChildAction;
    }> = [
      {
        type: "data-item",
        label: `Toevoegen: ${name}`,
        action: {
          type: "addModelChild",
          payload: { targetId },
        } satisfies UIAddModelChildAction,
      },
    ];

    if (canDelete) {
      for (const [index, childID] of childIDs.entries()) {
        content.push({
          type: "data-item",
          label: `Verwijderen: ${name} ${index + 1}`,
          action: {
            type: "deleteModelChild",
            payload: { targetId, childId: childID },
          } satisfies UIDeleteModelChildAction,
        });
      }
    }

    registerUIContext?.(actionSliceId, { type: "page", content });
    return () => {
      deregisterUIContext?.(actionSliceId);
    };
  }, [
    childIDs,
    canDelete,
    name,
    targetId,
    actionSliceId,
    registerUIContext,
    deregisterUIContext,
  ]);

  // Listen for addModelChild PubSub events
  useEffect(() => {
    const unsubscribe = pubSub.subscribe("addModelChild", (payload) => {
      if (payload?.targetId === targetId) {
        if (showAddCard) {
          handleAddFromCard();
        } else {
          handleAdd();
        }
      }
    });
    return unsubscribe;
  }, [targetId, showAddCard, pubSub, handleAdd, handleAddFromCard]);

  // Listen for deleteModelChild PubSub events — auto-confirm since
  // the agent already required user confirmation via trigger_page_action.
  useEffect(() => {
    const unsubscribe = pubSub.subscribe("deleteModelChild", (payload) => {
      if (payload?.targetId !== targetId || !payload.childId) return;
      if (!childIDs.includes(payload.childId)) return;
      // Mirror the canDelete guard: when optional is false, the last child
      // must not be removed — it would leave the list empty with no recovery path.
      if (!optional && childIDs.length <= 1) return;
      removeChild(payload.childId);
    });
    return unsubscribe;
  }, [targetId, childIDs, optional, pubSub, removeChild]);

  const getReplacerID = useCallback(
    (childID: string) => (parentID ? `${parentID}.${childID}` : childID),
    [parentID],
  );

  const pendingDeleteIndex = pendingDeleteID
    ? childIDs.indexOf(pendingDeleteID)
    : -1;
  const pendingDeleteName =
    pendingDeleteIndex >= 0 ? `${name} ${pendingDeleteIndex + 1}` : name;

  const addCardView = showAddCard ? (
    <Grid variant="twoColumn">
      {optional && (
        <OptionalCard
          onChange={handleOptionalChange}
          value={isOptionalChecked}
          label={optional.label}
        />
      )}
      <AddCard
        title={t("addItem", { name })}
        onClick={handleAddFromCard}
        disabled={isOptionalChecked}
      />
    </Grid>
  ) : null;

  if (variant === "flow") {
    return (
      <Stack reveal ref={ref}>
        {addCardView}
        {!showAddCard && (
          <Flex direction="column" gap="s">
            {childIDs.map((childID, index) => (
              <ExpansionPanel
                key={childID}
                id={getChildElementId(childID)}
                open={openPanels.has(childID)}
                onOpenChange={(open) =>
                  handlePanelOpenChange({ id: childID, open })
                }
                title={
                  <Flex width="stretch" gap="xs" align="center">
                    <Text variant="bodyM" bold grow>
                      {`${name} ${index + 1}`}
                    </Text>
                    {canDelete && openPanels.has(childID) && (
                      <Tooltip value={t("deleteItem", { name })}>
                        <IconButton
                          icon="delete"
                          variant="opaqueError"
                          size="xs"
                          border="default"
                          onClick={() => setPendingDeleteID(childID)}
                          style={{
                            pointerEvents: openPanels.has(childID)
                              ? "auto"
                              : "none",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                }
                trailing={
                  <Flex align="center" gap="xs">
                    {childErrors[childID] && !openPanels.has(childID) && (
                      <Tooltip value={t("formHasErrors")}>
                        <Icon icon="alertFilled" color="error" />
                      </Tooltip>
                    )}
                    <IconButton
                      as={openPanels.has(childID) ? "button" : "div"}
                      icon="chevronDown"
                      size="xs"
                      border="default"
                      variant="opaque"
                      onClick={
                        openPanels.has(childID)
                          ? () =>
                              handlePanelOpenChange({
                                id: childID,
                                open: false,
                              })
                          : undefined
                      }
                      rounded
                      style={{
                        transform: openPanels.has(childID)
                          ? "rotate(180deg)"
                          : undefined,
                      }}
                    />
                  </Flex>
                }
                variant="childModel"
                padding="s"
                gap="s"
                borderRadius="xs"
                border="none"
                forceMount
                customContent={
                  <div
                    ref={(el) => {
                      contentRefs.current[childID] = el;
                    }}
                  >
                    <FormGroupContext.Provider
                      value={{
                        key: getReplacerID(childID),
                        name: `${name} ${index + 1}`,
                        parentKey: outerGroup?.key,
                      }}
                    >
                      <TemplateParentContext.Provider
                        value={{
                          id: getReplacerID(childID),
                          data: data?.[childID],
                        }}
                      >
                        <Flex direction="column" gap="s">
                          <Spacing />
                          <TemplatesParser templates={templates} level={1} />
                          {trailingTemplates && (
                            <TemplatesParser
                              templates={trailingTemplates}
                              level={1}
                            />
                          )}
                        </Flex>
                      </TemplateParentContext.Provider>
                    </FormGroupContext.Provider>
                  </div>
                }
              />
            ))}
            <Button
              onClick={handleAdd}
              alignSelf="flex-end"
              leading={<Icon icon="add" size="s" />}
              size="xs"
              variant="secondary"
            >
              {t("addItem", { name })}
            </Button>
          </Flex>
        )}
        <DeleteConfirmationModal
          name={pendingDeleteName}
          active={!!pendingDeleteID}
          onCancel={() => setPendingDeleteID(null)}
          onConfirm={handleDeleteConfirm}
        />
      </Stack>
    );
  }

  const tabs = childIDs.map<Tab>((childID, index) => ({
    id: childID,
    label: (
      <Flex gap="xxs" align="center">
        {`${name} ${index + 1}`}
        {childErrors[childID] && (
          <Tooltip value={t("formHasErrors")}>
            <Icon icon="alertFilled" color="error" />
          </Tooltip>
        )}
      </Flex>
    ),
    content: (
      <div
        ref={(el) => {
          contentRefs.current[childID] = el;
        }}
      >
        <FormGroupContext.Provider
          value={{
            key: getReplacerID(childID),
            name: `${name} ${index + 1}`,
            parentKey: outerGroup?.key,
          }}
        >
          <TemplateParentContext.Provider
            value={{ id: getReplacerID(childID), data: data?.[childID] }}
          >
            <Flex direction="column" gap="s">
              <TemplatesParser templates={templates} level={1} />
              {canDelete && (
                <Flex justify="flex-end">
                  <Button
                    variant="secondaryError"
                    onClick={() => setPendingDeleteID(childID)}
                    alignSelf="flex-end"
                    leading={<Icon icon="delete" size="s" />}
                    size="xs"
                  >
                    {t("deleteItem", { name })}
                  </Button>
                </Flex>
              )}
              {trailingTemplates && (
                <TemplatesParser templates={trailingTemplates} level={1} />
              )}
            </Flex>
          </TemplateParentContext.Provider>
        </FormGroupContext.Provider>
      </div>
    ),
  }));

  return (
    <Stack reveal ref={ref}>
      {addCardView}
      {!showAddCard && (
        <>
          <Spacing height="s" />
          <TabsNavigation
            tabs={tabs}
            currentID={activeTab}
            onChange={setActiveTab}
            variant="outlined"
            color="background"
            forceMount
          >
            <Flex direction="column" padding="xxs">
              <Tooltip value={t("addItem", { name })}>
                <IconButton
                  icon="add"
                  variant="background"
                  size="xxs"
                  onClick={handleAdd}
                  rounded
                  border="default"
                />
              </Tooltip>
            </Flex>
          </TabsNavigation>
        </>
      )}
      <DeleteConfirmationModal
        name={pendingDeleteName}
        active={!!pendingDeleteID}
        onCancel={() => setPendingDeleteID(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Stack>
  );
};

/**
 * A template that renders multiple children, each rendering the same
 * set of templates with unique IDs. Includes a "+" button to add new children.
 *
 * Subscribes to `modelChildrenUpdate` pubSub events to allow external data
 * replacement (e.g. from WebSocket). When a matching update arrives, the inner
 * component remounts with the new data via a key change.
 *
 * - "tabs" variant: horizontal TabsNavigation
 * - "flow" variant: vertical ExpansionPanel per child
 */
export const ModelChildrenTemplate: FC<ModelChildrenTemplateProps> = ({
  ref,
  ...props
}) => {
  const pubSub = usePubSub();
  const dataOverrideRef = useRef<ChildDataRecord | undefined>(undefined);
  const [remountKey, setRemountKey] = useState(0);

  useEffect(() => {
    if (!props.id) return;

    const unsubscribe = pubSub.subscribe("modelChildrenUpdate", (payload) => {
      if (payload?.id === props.id) {
        dataOverrideRef.current = payload.data;
        setRemountKey((k) => k + 1);
      }
    });

    return unsubscribe;
  }, [props.id, pubSub]);

  return (
    <ModelChildrenTemplateInner
      key={remountKey}
      {...props}
      data={dataOverrideRef.current ?? props.data}
      ref={ref}
    />
  );
};
